import { useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout } from '../components/Layout';
import { CalendarCheck, Clock, MapPin, CheckCircle2, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CounselorMeetingsPage() {
  const { getAuthHeader } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState('');
  const [msg, setMsg]         = useState('');

  const load = () => axios.get(`${API}/counselor/my-meetings`, getAuthHeader())
    .then(r => setData(r.data))
    .catch(() => setData(null))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line

  const claim = async (slot) => {
    setBusy(slot.id); setMsg('');
    try {
      await axios.post(`${API}/counselor/my-meetings/${slot.id}/claim`, {}, getAuthHeader());
      setMsg(`Booked ${slot.date} at ${slot.start_time}`);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Could not book that time');
    } finally { setBusy(''); }
  };

  const card = 'rounded-2xl border border-white/10 backdrop-blur-xl';
  const bg = { background: 'rgba(13,27,46,0.72)' };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-7 pb-16" data-testid="counselor-meetings-page">
        <section className="relative overflow-hidden rounded-3xl border px-7 py-8 md:px-10"
          style={{ background: 'linear-gradient(135deg,#0b1f38,#0a1729)', borderColor: 'rgba(125,211,252,0.20)' }}>
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-300">Counselor time</span>
          <h1 className="font-outfit mt-3 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Book time with your counselor
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-300/90">
            {data?.counselor_name
              ? `${data.counselor_name} publishes open times here. Claim one, no email needed.`
              : 'Open times published by your counselor appear here.'}
          </p>
        </section>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-300 border-t-transparent" />
          </div>
        ) : (
          <>
            {msg && <p className="text-sm font-semibold text-emerald-400" data-testid="meeting-msg">{msg}</p>}

            <div className={`${card} overflow-hidden`} style={bg}>
              <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-6 py-4">
                <CalendarCheck className="h-4 w-4 text-emerald-300" />
                <h2 className="font-outfit text-base font-semibold text-white">My meetings</h2>
              </div>
              {(data?.my_meetings || []).length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-slate-300">Nothing booked yet.</p>
              ) : (
                <div className="divide-y divide-white/[0.05]">
                  {data.my_meetings.map(m => (
                    <div key={m.id} className="flex flex-wrap items-center gap-4 px-6 py-4"
                      data-testid={`my-meeting-${m.id}`}>
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white">{m.date} at {m.start_time}</p>
                        <p className="mt-0.5 text-xs text-slate-300">{m.topic} · {m.duration_min} min</p>
                      </div>
                      <span className="flex items-center gap-1.5 text-xs text-slate-300">
                        <User className="h-3.5 w-3.5" /> {m.counselor_name}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-300">
                        <MapPin className="h-3.5 w-3.5" /> {m.location}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={`${card} overflow-hidden`} style={bg}>
              <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-6 py-4">
                <Clock className="h-4 w-4 text-sky-300" />
                <h2 className="font-outfit text-base font-semibold text-white">Open times</h2>
                <span className="ml-auto text-[11px] text-slate-300">{(data?.available || []).length} available</span>
              </div>
              {(data?.available || []).length === 0 ? (
                <p className="px-6 py-10 text-center text-sm text-slate-300">
                  No open times right now, check back, or message your counselor.
                </p>
              ) : (
                <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
                  {data.available.map(s => (
                    <button key={s.id} onClick={() => claim(s)} disabled={busy === s.id}
                      data-testid={`claim-slot-${s.id}`}
                      className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-left
                        hover:border-sky-300/50 hover:bg-white/[0.08] disabled:opacity-50 transition-colors">
                      <p className="text-lg font-semibold text-white">{s.start_time}</p>
                      <p className="text-xs text-slate-300">{s.date} · {s.duration_min} min</p>
                      <p className="mt-2 text-[11px] text-sky-300">{busy === s.id ? 'Booking…' : 'Claim this time'}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
